import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import type React from 'react'
import { useCallback, useMemo, useRef } from 'react'

import { ResizableDrawer } from '@/components/ui/resizable-drawer.organism'
import { getStripe } from '@/utils/stripe.util'

interface EmbeddedCheckoutDrawerProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  clientSecret: string | null
  onComplete: () => void
}

interface EmbeddedCheckoutContentProps {
  clientSecret: string
  onComplete: () => void
}

const EmbeddedCheckoutContent: React.FC<EmbeddedCheckoutContentProps> = ({
  clientSecret,
  onComplete,
}) => {
  const stripePromise = useMemo(() => getStripe(), [])
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const stableOnComplete = useCallback(() => {
    onCompleteRef.current()
  }, [])

  const options = useMemo(
    () => ({ clientSecret, onComplete: stableOnComplete }),
    [clientSecret, stableOnComplete]
  )

  return (
    <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
      <EmbeddedCheckout className="embedded-checkout-form" />
    </EmbeddedCheckoutProvider>
  )
}

export const EmbeddedCheckoutDrawer: React.FC<EmbeddedCheckoutDrawerProps> = ({
  isOpen,
  onOpenChange,
  clientSecret,
  onComplete,
}) => (
  <ResizableDrawer
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    title="Complete Payment"
    description="Enter your payment details below"
  >
    <div className="embedded-checkout-wrapper flex h-full w-full flex-col overflow-y-auto p-4">
      {isOpen && clientSecret && (
        <EmbeddedCheckoutContent clientSecret={clientSecret} onComplete={onComplete} />
      )}
    </div>
  </ResizableDrawer>
)
